import { CareOSLoader } from '@/components/ui/careos-loader';
import { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, ShieldCheck, FileText } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

interface LegalDoc {
  slug: string;
  version: string;
  title: string;
  content_md: string;
}

type RepresentativeRole = 'owner' | 'admin' | 'procurator';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: string;
  companyName: string;
  onSuccess?: () => void;
}

const SLUGS = ['b2b-contract', 'dpa'] as const;

/**
 * Sequential acceptance flow for B2B contract + DPA, mirroring the PF
 * `LegalReacceptanceModal` UX: one document per step with full content,
 * then a final step collecting representative info and submitting.
 */
export function CompanyContractAcceptanceFlow({
  open,
  onOpenChange,
  companyId,
  companyName,
  onSuccess,
}: Props) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [docs, setDocs] = useState<LegalDoc[]>([]);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0); // 0..docs.length-1 = doc steps; docs.length = representative step
  const [submitting, setSubmitting] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const viewport = scrollRef.current?.querySelector<HTMLElement>(
      '[data-radix-scroll-area-viewport]',
    );
    if (viewport) viewport.scrollTop = 0;
  }, [step]);

  const [representativeRole, setRepresentativeRole] = useState<RepresentativeRole>('owner');
  const [representativeName, setRepresentativeName] = useState(
    (user?.user_metadata?.full_name as string | undefined) ?? '',
  );

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setStep(0);
    (async () => {
      const { data } = await supabase
        .from('legal_documents')
        .select('slug, version, title, content_md')
        .in('slug', SLUGS as unknown as string[])
        .eq('locale', 'pt-BR')
        .is('superseded_at', null)
        .order('effective_at', { ascending: false });

      if (cancelled) return;

      const bySlug = new Map<string, LegalDoc>();
      for (const d of (data as LegalDoc[]) ?? []) {
        if (!bySlug.has(d.slug)) bySlug.set(d.slug, d);
      }
      setDocs(SLUGS.map((s) => bySlug.get(s)).filter(Boolean) as LegalDoc[]);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const totalSteps = docs.length + 1; // +1 for representative step
  const isRepresentativeStep = step >= docs.length;
  const currentDoc = docs[step];

  const handleAcceptDoc = () => {
    if (step < docs.length - 1) {
      setStep(step + 1);
    } else {
      // move to representative step
      setStep(docs.length);
    }
  };

  const handleSubmit = async () => {
    if (!representativeName.trim()) {
      toast.error('Informe o nome do representante');
      return;
    }
    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        'record-company-contract-acceptance',
        {
          body: {
            company_id: companyId,
            representative_role: representativeRole,
            representative_name: representativeName.trim(),
          },
        },
      );
      if (error || (data as { error?: string })?.error) {
        throw new Error((data as { error?: string })?.error ?? error?.message ?? 'unknown');
      }
      await queryClient.invalidateQueries({ queryKey: ['company-contract-status', companyId] });
      toast.success('Aceite registrado com sucesso');
      onSuccess?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(`Não foi possível registrar o aceite: ${(err as Error).message}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !submitting && onOpenChange(o)}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isRepresentativeStep ? (
              <ShieldCheck className="w-5 h-5 text-primary" />
            ) : (
              <FileText className="w-5 h-5 text-primary" />
            )}
            {isRepresentativeStep
              ? 'Confirmação do representante'
              : currentDoc?.title ?? 'Documento'}
          </DialogTitle>
          <DialogDescription>
            {isRepresentativeStep ? (
              <>
                Você está finalizando o aceite em nome de <strong>{companyName}</strong>.
              </>
            ) : (
              <>
                {currentDoc && `Versão ${currentDoc.version} · `}
                Etapa {step + 1} de {totalSteps}
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <CareOSLoader size="md" />
          </div>
        ) : isRepresentativeStep ? (
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              Você declara estar autorizado a representar <strong>{companyName}</strong> e
              confirma o aceite do Contrato B2B e do DPA nas versões apresentadas.
            </p>

            <div className="space-y-2">
              <Label htmlFor="representative-role">Papel do representante</Label>
              <Select
                value={representativeRole}
                onValueChange={(v) => setRepresentativeRole(v as RepresentativeRole)}
              >
                <SelectTrigger id="representative-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="owner">Sócio / Titular</SelectItem>
                  <SelectItem value="admin">Administrador</SelectItem>
                  <SelectItem value="procurator">Procurador</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="representative-name">Nome do representante</Label>
              <Input
                id="representative-name"
                value={representativeName}
                onChange={(e) => setRepresentativeName(e.target.value)}
                placeholder="Nome completo"
              />
            </div>
          </div>
        ) : currentDoc ? (
          <ScrollArea ref={scrollRef} className="h-[55vh] border rounded-md p-4">
            <article className="prose prose-sm prose-neutral dark:prose-invert max-w-none">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {currentDoc.content_md}
              </ReactMarkdown>
            </article>
          </ScrollArea>
        ) : (
          <p className="text-sm text-muted-foreground py-8 text-center">
            Documento indisponível.
          </p>
        )}

        <div className="flex justify-between gap-2 pt-2">
          {!isRepresentativeStep && step > 0 ? (
            <Button variant="ghost" onClick={() => setStep(step - 1)} disabled={loading}>
              Voltar
            </Button>
          ) : isRepresentativeStep ? (
            <Button
              variant="ghost"
              onClick={() => setStep(docs.length - 1)}
              disabled={submitting}
            >
              Voltar
            </Button>
          ) : (
            <span />
          )}

          {isRepresentativeStep ? (
            <Button onClick={handleSubmit} disabled={submitting || !representativeName.trim()}>
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Confirmar aceite
            </Button>
          ) : (
            <Button onClick={handleAcceptDoc} disabled={loading || !currentDoc}>
              {step < docs.length - 1 ? 'Aceitar e continuar' : 'Aceitar e prosseguir'}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
